import { createContext, ReactNode, use, useMemo, useState } from "react";

import { useAutoBookmark } from "lib/bookmarks/use-auto-bookmark";
import { ContentContext } from "lib/content/use-content";
import { getConfig } from "lib/get-config";
import { ContentLayoutContext } from "lib/layout/use-content-layout";
import { useToc } from "lib/toc/use-toc";
import type { NavigationBookmark } from "lib/types";

import { chapterByOrder } from "./paths";
import { usePaths } from "./use-paths";

interface NavigationContextType {
  bookmark: NavigationBookmark | null;
  updateBookmark: (xpointer: string, targetPercent?: number) => void;
  totalPages: number;
  page: number;
  nextPage: () => void;
  prevPage: () => void;
  goToPage: (page: number) => void;
  isNavigationEnabled: boolean;
  isLayoutComplete: boolean;
  dragOffset: number;
  setDragOffset: (offset: number) => void;
}

export const NavigationContext = createContext<
  NavigationContextType | undefined
>(undefined);
NavigationContext.displayName = "NavigationContext";

type ProviderProps = {
  children: ReactNode;
};

export function NavigationProvider({ children }: ProviderProps) {
  const { user, chapter } = getConfig();
  const [dragOffset, setDragOffset] = useState(0);
  const contentParts = use(ContentContext)!.contentParts;
  const { initialContentParts, content } = use(ContentContext)!;
  const { totalPages, layout } = use(ContentLayoutContext)!;
  const {
    pageToXpointer,
    xpointerToLocation,
    xpointerToPage,
    indexToXpointer,
  } = usePaths();
  const { toc } = useToc();

  const totalParts = toc?.Parts?.length ?? 0;
  const isLayoutComplete =
    initialContentParts.length >= totalParts ||
    (layout?.mode === "virtual" && content.length >= totalParts);
  const isNavigationEnabled = layout !== null;

  const { autoBookmark, createAutoBookmark } = useAutoBookmark();
  const [bookmark, setBookmark] = useState<NavigationBookmark | null>(null);

  if (!bookmark && layout && totalPages > 0) {
    const chapterTarget = chapter && toc ? chapterByOrder(toc, chapter) : null;
    const xpointer =
      (chapterTarget && indexToXpointer(chapterTarget.s)) ??
      autoBookmark?.data?.xpointer ??
      pageToXpointer(0);
    if (xpointer) {
      setBookmark({ xpointer, percent: null });
    }
  }

  const page = useMemo(() => {
    const xpointer = bookmark?.xpointer;
    if (totalPages === 0 || !xpointer) {
      return 0;
    }
    const location = xpointerToLocation(xpointer);
    if (!location) {
      return 0;
    }

    if (
      isLayoutComplete &&
      bookmark?.percent != null &&
      location.startPage !== location.endPage
    ) {
      const bookmarkPage = Math.round((bookmark.percent / 100) * totalPages);
      if (
        bookmarkPage >= location.startPage &&
        bookmarkPage <= location.endPage
      ) {
        return bookmarkPage;
      }
    }

    return xpointerToPage(xpointer) ?? location.startPage;
  }, [
    bookmark,
    totalPages,
    xpointerToLocation,
    xpointerToPage,
    isLayoutComplete,
  ]);

  function updateBookmark(xpointer: string, targetPercent?: number) {
    // Переключаем часть виртуального контента, если нужно
    if (layout?.mode === "virtual" && contentParts) {
      const targetPart = contentParts.findPartIndexByXpointer(xpointer);
      if (targetPart !== contentParts.currentPartIndex) {
        contentParts.setCurrentPartIndex(targetPart);
      }
    }

    // Процент используется только когда явно передан (для навигации к закладкам)
    // Для TOC/ссылок процент не передаётся - используется startPage элемента
    const newPercent = targetPercent ?? null;

    setBookmark({ xpointer, percent: newPercent });

    // Сохраняем autobookmark в virtual mode
    if (user && totalPages > 0 && layout?.mode === "virtual") {
      const percentForAutoBookmark =
        newPercent ??
        (() => {
          const location = xpointerToLocation(xpointer);
          if (!location) return 0;
          return (location.startPage / totalPages) * 100;
        })();
      createAutoBookmark(xpointer, percentForAutoBookmark);
    }
  }

  function validatePage(p: number) {
    return Math.max(0, Math.min(totalPages - 1, p));
  }

  function nextPage() {
    goToPage(validatePage(page + 1));
  }

  function prevPage() {
    goToPage(validatePage(page - 1));
  }

  function goToPage(targetPage: number) {
    if (!isNavigationEnabled || !layout) return;
    const xpointer = pageToXpointer(targetPage);
    if (!xpointer) {
      console.warn("no xpointer for page", targetPage);
      return;
    }

    const targetPercent = (targetPage / totalPages) * 100;
    updateBookmark(xpointer, targetPercent);
  }

  return (
    <NavigationContext
      value={{
        bookmark,
        updateBookmark,
        totalPages,
        page,
        nextPage,
        prevPage,
        goToPage,
        isNavigationEnabled,
        isLayoutComplete,
        dragOffset,
        setDragOffset,
      }}
    >
      {children}
    </NavigationContext>
  );
}
